import { differenceInCalendarDays, format as fmt, isValid, parseISO } from 'date-fns';
import type { RegionalSettings } from '@modules/base/desktop/api/types';
import { clock } from './clock';

export interface Formatters {
  date: (iso: string | null | undefined) => string;
  dateShort: (iso: string | null | undefined) => string;
  dateTime: (iso: string | null | undefined) => string;
  time: (iso: string | null | undefined) => string;
  /** "Today", "Tomorrow", "Yesterday", "In 3 days", "5 days ago". */
  relativeDay: (iso: string | null | undefined) => string;
  daysSince: (iso: string) => number;
  /** Whole years old, or null when the date of birth is not on file. */
  age: (dob: string | null | undefined) => number | null;
  weight: (kg: number | undefined) => string;
  height: (cm: number | undefined) => string;
  megabytes: (mb: number) => string;
  dateFormat: string;
}

export function makeFormatters(r: RegionalSettings): Formatters {
  const dateFormat = r.dateFormat;
  const shortFormat = r.dateFormat === 'MM/dd/yyyy' ? 'MMM d' : 'd MMM';
  const hour12 = r.timeFormat === '12h';

  function toDate(iso: string): Date | null {
    const d = parseISO(iso);
    return isValid(d) ? d : null;
  }

  function timeInZone(iso: string): string {
    const d = toDate(iso);
    return d ? new Intl.DateTimeFormat('en-GB', { timeZone: r.timeZone, hour: '2-digit', minute: '2-digit', hour12 }).format(d) : '';
  }

  /** Date part of a timestamp as seen in the organization time zone. */
  function zonedDatePart(iso: string): string {
    if (iso.length <= 10) return iso;
    const d = toDate(iso);
    if (!d) return iso.slice(0, 10);
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: r.timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    return parts;
  }

  const date: Formatters['date'] = (iso) => {
    if (!iso) return '—';
    const d = toDate(zonedDatePart(iso));
    return d ? fmt(d, dateFormat) : '—';
  };

  return {
    dateFormat,
    date,
    dateShort: (iso) => {
      if (!iso) return '—';
      const d = toDate(zonedDatePart(iso));
      return d ? fmt(d, shortFormat) : '—';
    },
    dateTime: (iso) => (iso ? `${date(iso)} ${timeInZone(iso)}` : '—'),
    time: (iso) => (iso ? timeInZone(iso) : '—'),
    relativeDay: (iso) => {
      if (!iso) return '—';
      const d = toDate(zonedDatePart(iso));
      if (!d) return '—';
      const diff = differenceInCalendarDays(d, parseISO(clock.today()));
      if (diff === 0) return 'Today';
      if (diff === 1) return 'Tomorrow';
      if (diff === -1) return 'Yesterday';
      return diff > 0 ? `In ${diff} days` : `${-diff} days ago`;
    },
    daysSince: (iso) => differenceInCalendarDays(parseISO(clock.today()), parseISO(zonedDatePart(iso))),
    age: (dob) => {
      if (!dob) return null;
      const b = parseISO(dob);
      const t = parseISO(clock.today());
      let a = t.getFullYear() - b.getFullYear();
      if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--;
      return a;
    },
    weight: (kg) => (kg === undefined ? '—' : r.weightUnit === 'kg' ? `${kg} kg` : `${Math.round(kg * 2.20462)} lb`),
    height: (cm) =>
      cm === undefined ? '—' : r.heightUnit === 'cm' ? `${cm} cm` : `${Math.floor(cm / 2.54 / 12)}′ ${Math.round((cm / 2.54) % 12)}″`,
    megabytes: (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`),
  };
}
